/**
 * ClientsList - Liste des clients avec filtres et sélection
 * Refactorisé en sous-composants pour améliorer la maintenabilité
 */
import React, { useState, useEffect, useMemo, useRef } from 'react';
import ClientForm from '../ClientForm';
import CSVImport from '../CSVImport';
import DistanceBatchCalculator from '../DistanceBatchCalculator';
import { PageHeader } from '../common';
import { API_ENDPOINTS } from '../../utils/apiConfig';
import { useNotifications } from '../../contexts/NotificationContext';

// Sous-composants
import ClientCard from './ClientCard';
import ClientsFilters from './ClientsFilters';
import BulkQualifyModal from './BulkQualifyModal';
import ConfirmModal from './ConfirmModal';
import SelectionBar from './SelectionBar';

const PAGE_SIZE = 24;

const ClientsList = ({ token, onNavigateToLocations }) => {
  const { showSuccess, showError } = useNotifications();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showDistanceCalc, setShowDistanceCalc] = useState(false);
  const [showBulkQualify, setShowBulkQualify] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterGeocoding, setFilterGeocoding] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const listRef = useRef(null);
  const [typeStats, setTypeStats] = useState(null);
  const [selectedClients, setSelectedClients] = useState([]);
  const [confirmModal, setConfirmModal] = useState({ show: false, title: '', message: '', onConfirm: null });

  const loadClients = async () => {
    try {
      setLoading(true);
      const response = await fetch(API_ENDPOINTS.CLIENTS_ALL, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (data.success) {
        setClients(data.data);
        setError('');
      } else {
        setError(data.error);
      }
    } catch (err) {
      setError('Erreur de connexion au serveur');
    } finally {
      setLoading(false);
    }
  };

  const loadTypeStats = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.CLIENTS_TYPES_STATS, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      if (data.success) {
        setTypeStats(data.data);
      }
    } catch (err) {
      console.error('Erreur chargement stats types:', err);
    }
  };

  const deleteClient = (clientId, clientName) => {
    setConfirmModal({
      show: true,
      title: 'Supprimer le client',
      message: `Êtes-vous sûr de vouloir supprimer le client "${clientName}" ?`,
      onConfirm: async () => {
        setConfirmModal({ ...confirmModal, show: false });
        try {
          const response = await fetch(API_ENDPOINTS.CLIENT_BY_ID(clientId), {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });

          const data = await response.json();

          if (data.success) {
            await loadClients();
            showSuccess(data.message);
          } else {
            showError('Erreur: ' + data.error);
          }
        } catch (err) {
          showError('Erreur de connexion au serveur');
        }
      }
    });
  };

  const filteredClients = useMemo(() => {
    const search = searchTerm.toLowerCase().trim();
    return clients.filter(client => {
      const matchesSearch = !search ||
                           client.name?.toLowerCase().includes(search) ||
                           client.code?.toLowerCase().includes(search) ||
                           client.city?.toLowerCase().includes(search) ||
                           client.postal_code?.toLowerCase().includes(search) ||
                           client.contact_name?.toLowerCase().includes(search) ||
                           client.notes?.toLowerCase().includes(search);
      const matchesStatus = filterStatus === '' ||
                           (filterStatus === 'active' && client.is_active) ||
                           (filterStatus === 'inactive' && !client.is_active);
      const matchesType = filterType === '' ||
                         (filterType === 'non_qualifie' && !client.client_type) ||
                         client.client_type === filterType;
      const matchesGeocoding = filterGeocoding === '' ||
                              (filterGeocoding === 'issues' && (client.geocoding_status === 'warning' || client.geocoding_status === 'error')) ||
                              (filterGeocoding === 'warning' && client.geocoding_status === 'warning') ||
                              (filterGeocoding === 'error' && client.geocoding_status === 'error') ||
                              (filterGeocoding === 'ok' && client.geocoding_status === 'ok') ||
                              (filterGeocoding === 'pending' && (!client.geocoding_status || client.geocoding_status === 'pending'));

      return matchesSearch && matchesStatus && matchesType && matchesGeocoding;
    });
  }, [clients, searchTerm, filterStatus, filterType, filterGeocoding]);

  const totalPages = Math.ceil(filteredClients.length / PAGE_SIZE);
  const paginatedClients = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredClients.slice(start, start + PAGE_SIZE);
  }, [filteredClients, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterStatus, filterType, filterGeocoding]);

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [currentPage]);

  useEffect(() => {
    loadClients();
    loadTypeStats();
  }, [token]);

  const toggleClientSelection = (clientId) => {
    setSelectedClients(prev =>
      prev.includes(clientId)
        ? prev.filter(id => id !== clientId)
        : [...prev, clientId]
    );
  };

  const selectAllVisible = () => {
    const visibleIds = paginatedClients.map(c => c.id);
    setSelectedClients(prev => {
      const allSelected = visibleIds.every(id => prev.includes(id));
      if (allSelected) {
        return prev.filter(id => !visibleIds.includes(id));
      }
      return [...new Set([...prev, ...visibleIds])];
    });
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <p>Chargement des clients...</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px' }}>
      <PageHeader
        icon="🏰"
        title="Gestion des Clients"
        color="orange"
        actions={
          <>
            {onNavigateToLocations && (
              <button
                onClick={onNavigateToLocations}
                style={{
                  background: 'transparent',
                  color: '#17a2b8',
                  border: '2px solid #17a2b8',
                  padding: '8px 16px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '500',
                  fontSize: '14px',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = '#17a2b8';
                  e.target.style.color = 'white';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'transparent';
                  e.target.style.color = '#17a2b8';
                }}
              >
                Lieux de mise
              </button>
            )}
            <button
              onClick={() => setShowDistanceCalc(true)}
              style={{
                background: 'linear-gradient(135deg, #28a745, #20c997)',
                color: 'white',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '600'
              }}
            >
              Calculer KM
            </button>
            <button
              onClick={() => setShowImport(true)}
              style={{
                background: 'linear-gradient(135deg, #6c757d, #5a6268)',
                color: 'white',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '600'
              }}
            >
              Import CSV
            </button>
            <button
              onClick={() => { setEditingClient(null); setShowForm(true); }}
              style={{
                background: 'linear-gradient(135deg, #fd7e14, #e85d04)',
                color: 'white',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '600'
              }}
            >
              + Nouveau client
            </button>
          </>
        }
      />

      {error && (
        <div style={{ background: '#f8d7da', color: '#721c24', padding: '15px', borderRadius: '8px', marginBottom: '20px' }}>
          {error}
        </div>
      )}

      <SelectionBar
        count={selectedClients.length}
        onQualify={() => setShowBulkQualify(true)}
        onCancel={() => setSelectedClients([])}
      />

      <ClientsFilters
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        filterType={filterType}
        onFilterTypeChange={setFilterType}
        filterStatus={filterStatus}
        onFilterStatusChange={setFilterStatus}
        filterGeocoding={filterGeocoding}
        onFilterGeocodingChange={setFilterGeocoding}
        typeStats={typeStats}
        paginatedClients={paginatedClients}
        selectedClients={selectedClients}
        onSelectAllVisible={selectAllVisible}
        totalCount={filteredClients.length}
      />

      {/* Liste des clients */}
      <div ref={listRef} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 'clamp(15px, 4vw, 20px)', scrollMarginTop: '80px' }}>
        {paginatedClients.map(client => (
          <ClientCard
            key={client.id}
            client={client}
            isSelected={selectedClients.includes(client.id)}
            onToggleSelection={toggleClientSelection}
            onEdit={(c) => { setEditingClient(c); setShowForm(true); }}
            onDelete={deleteClient}
          />
        ))}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '10px',
          marginTop: '24px',
          padding: '16px',
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.08)'
        }}>
          <button
            onClick={() => setCurrentPage(1)}
            disabled={currentPage === 1}
            style={{
              padding: '8px 12px',
              background: currentPage === 1 ? '#e9ecef' : '#fd7e14',
              color: currentPage === 1 ? '#6c757d' : 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
              fontWeight: '600'
            }}
          >
            ««
          </button>
          <button
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            style={{
              padding: '8px 16px',
              background: currentPage === 1 ? '#e9ecef' : '#fd7e14',
              color: currentPage === 1 ? '#6c757d' : 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
              fontWeight: '600'
            }}
          >
            Précédent
          </button>

          <span style={{ padding: '0 16px', fontWeight: '600', color: '#495057' }}>
            Page {currentPage} / {totalPages}
          </span>
          <span style={{ color: '#6c757d', fontSize: '14px' }}>
            ({filteredClients.length} clients)
          </span>

          <button
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            style={{
              padding: '8px 16px',
              background: currentPage === totalPages ? '#e9ecef' : '#fd7e14',
              color: currentPage === totalPages ? '#6c757d' : 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
              fontWeight: '600'
            }}
          >
            Suivant
          </button>
          <button
            onClick={() => setCurrentPage(totalPages)}
            disabled={currentPage === totalPages}
            style={{
              padding: '8px 12px',
              background: currentPage === totalPages ? '#e9ecef' : '#fd7e14',
              color: currentPage === totalPages ? '#6c757d' : 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
              fontWeight: '600'
            }}
          >
            »»
          </button>
        </div>
      )}

      {filteredClients.length === 0 && (
        <div style={{ textAlign: 'center', padding: '50px', color: '#666' }}>
          Aucun client trouvé
        </div>
      )}

      {/* Modales */}
      {showForm && (
        <ClientForm
          token={token}
          client={editingClient}
          onClose={() => setShowForm(false)}
          onSave={() => {
            setShowForm(false);
            loadClients();
          }}
        />
      )}

      {showImport && (
        <CSVImport
          token={token}
          importType="clients"
          apiEndpoint={API_ENDPOINTS.CLIENTS_IMPORT}
          onClose={() => setShowImport(false)}
          onImportComplete={() => {
            loadClients();
          }}
        />
      )}

      {showDistanceCalc && (
        <DistanceBatchCalculator
          token={token}
          entityType="clients"
          onClose={() => setShowDistanceCalc(false)}
          onComplete={() => loadClients()}
        />
      )}

      {showBulkQualify && (
        <BulkQualifyModal
          token={token}
          selectedClients={selectedClients}
          clients={clients.filter(c => selectedClients.includes(c.id))}
          onClose={() => setShowBulkQualify(false)}
          onSuccess={() => {
            setShowBulkQualify(false);
            setSelectedClients([]);
            loadClients();
            loadTypeStats();
          }}
        />
      )}

      <ConfirmModal
        show={confirmModal.show}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal({ ...confirmModal, show: false })}
      />
    </div>
  );
};

export default ClientsList;
